import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import * as z from 'zod'
import useMutationDisable2FA from '../../api/hooks/useMutationDisable2FA'
import { useQueryClient } from '@tanstack/react-query'
import { AuthQueryKey } from '@/modules/auth/keys'
import { OperatorQueryKeys } from '@/modules/manage-member/keys'

const disable2FASchema = z.object({
	totpCode: z
		.string({
			required_error: 'รหัส TOTP ต้องมีอย่างน้อย 6 ตัว',
			invalid_type_error: 'รหัส TOTP ต้องมีอย่างน้อย 6 ตัว',
		})
		.min(6, 'รหัส TOTP ต้องมีอย่างน้อย 6 ตัว')
		.max(6, 'รหัส TOTP ต้องมีอย่างน้อย 6 ตัว'),
})

type Disable2FADialogProps = {
	open: boolean
	onOpenChange: (open: boolean) => void
	id: string
}

export default function Disable2FADialog({
	open,
	onOpenChange,
	id,
}: Disable2FADialogProps) {
	const { mutate: disable2FA, isPending: isDisabling2FA } =
		useMutationDisable2FA(id)
	const queryClient = useQueryClient()

	const form = useForm<z.infer<typeof disable2FASchema>>({
		resolver: zodResolver(disable2FASchema),
	})

	const onSubmit = (data: z.infer<typeof disable2FASchema>) => {
		disable2FA(data, {
			onSuccess: () => {
				toast.success('ปิดใช้งาน 2FA สำเร็จ')
				form.reset()
				onOpenChange(false)
				queryClient.invalidateQueries({
					queryKey: [AuthQueryKey.Profile],
				})
				queryClient.invalidateQueries({
					queryKey: [OperatorQueryKeys.GetMemberList],
				})
			},
			onError: (error) => {
				showErrorToast(error)
			},
		})
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>ปิดใช้งาน Two-Factor Authentication</DialogTitle>
					<DialogDescription>
						กรุณากรอกรหัส TOTP เพื่อยืนยันการปิดใช้งาน 2FA
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<FormField
							control={form.control}
							name="totpCode"
							render={({ field }) => (
								<FormItem>
									<FormLabel>รหัส TOTP</FormLabel>
									<FormControl>
										<Input placeholder="กรอกรหัส TOTP..." {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<DialogFooter>
							<Button
								type="submit"
								variant="destructive"
								disabled={isDisabling2FA}
							>
								ปิดใช้งาน 2FA
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
